import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { match } from 'ts-pattern'
import { programsNewCollection } from '@/reducers/programsNew'
import {
  BehavioralEnrollment,
  ChargerEnrollment,
  EnrollmentOverview,
  ThermostatEnrollment,
  VehicleEnrollment,
  EnergySystemEnrollment,
  DeviceEligibilityOverview,
} from '@/types/programs-new/programs-new'
import {
  chargerEnrollmentCollection,
  energySystemEnrollmentCollection,
  thermostatEnrollmentCollection,
  vehicleEnrollmentCollection,
} from '@/reducers/deviceProgramEnrollments'
import getProgramEnrollmentExpiration from '@/utils/utility-program-helpers/getProgramEnrollmentExpiration'

const deviceTypeToEnrollmentMap: Record<DeviceType, keyof EnrollmentOverview> =
  {
    vehicle: 'vehicle_enrollments',
    thermostat: 'thermostat_enrollments',
    home: 'behavioral_enrollments',
    vehicleCharger: 'charger_enrollments',
    energySystem: 'energy_system_enrollments',
  }

const deviceTypeToEligibilityMap: Record<
  DeviceType,
  keyof DeviceEligibilityOverview['devices']
> = {
  vehicle: 'vehicles',
  thermostat: 'thermostats',
  home: 'behavioral',
  vehicleCharger: 'chargers',
  energySystem: 'energy_systems',
}

type DeviceTypeToEnrollmentArrayMap = {
  vehicle: VehicleEnrollment[]
  thermostat: ThermostatEnrollment[]
  home: BehavioralEnrollment[]
  vehicleCharger: ChargerEnrollment[]
  energySystem: EnergySystemEnrollment[]
}

type UseDeviceEnrollmentsOptions = {
  includeExpired?: boolean
}

const makeOptions = (options?: UseDeviceEnrollmentsOptions) => {
  return {
    includeExpired: false,
    ...options,
  }
}

/**
 * Gets the active and inactive enrollments associated with a device
 * @param deviceType - The device type we're looking for (vehicle, thermostats, etc.)
 * @param deviceId - The ID of the device in the DB
 */
export default function useDeviceEnrollments(
  deviceType: DeviceType,
  deviceId: string | number,
  options?: UseDeviceEnrollmentsOptions,
) {
  const opts = makeOptions(options)
  const { data, isLoadingOrIdle } = programsNewCollection.useFetch()
  const {
    data: vehicleEnrollmentData,
    isLoadingOrIdle: vehicleEnrollmentIsLoadingOrIdle,
  } = vehicleEnrollmentCollection.useFetch()
  const {
    data: chargerEnrollmentData,
    isLoadingOrIdle: chargerEnrollmentIsLoadingOrIdle,
  } = chargerEnrollmentCollection.useFetch()
  const {
    data: thermostatEnrollmentData,
    isLoadingOrIdle: thermostatEnrollmentIsLoadingOrIdle,
  } = thermostatEnrollmentCollection.useFetch()
  const {
    data: energySystemEnrollmentData,
    isLoadingOrIdle: energySystemEnrollmentIsLoadingOrIdle,
  } = energySystemEnrollmentCollection.useFetch()

  const field = deviceTypeToEnrollmentMap[deviceType]

  const deviceEnrollments = data
    ?.map((p) => {
      const relevantEnrollments = p.enrollment_overview?.[field] as
        | DeviceTypeToEnrollmentArrayMap[DeviceType]
        | undefined

      const enrollment = match(deviceType)
        .with('vehicle', () => {
          const legacyEnrollment = vehicleEnrollmentData?.find(
            (e) => e.vehicle_id === deviceId,
          )
          if (legacyEnrollment) {
            return {
              ...legacyEnrollment,
              utility_program_id:
                legacyEnrollment.utility_program_id?.toString(),
              unenrolled_reasons: '',
              unenrolled_source: '',
              group: legacyEnrollment.group ?? {},
            } as VehicleEnrollment
          }
          const enrollment = relevantEnrollments?.find(
            (e) => (e as VehicleEnrollment).vehicle_id === deviceId,
          )
          return enrollment
            ? {
                ...enrollment,
                group: enrollment.group ?? {},
              }
            : enrollment
        })
        .with('thermostat', () => {
          const legacyEnrollment = thermostatEnrollmentData?.find(
            (e) => e.thermostat_id === deviceId,
          )
          if (legacyEnrollment) {
            return {
              ...legacyEnrollment,
              utility_program_id:
                legacyEnrollment.utility_program_id?.toString(),
              unenrolled_reasons: '',
              unenrolled_source: '',
              group: legacyEnrollment.group ?? {},
            } as ThermostatEnrollment
          }
          const enrollment = relevantEnrollments?.find(
            (e) => (e as ThermostatEnrollment).thermostat_id === deviceId,
          )
          return enrollment
            ? {
                ...enrollment,
                group: enrollment.group ?? {},
              }
            : enrollment
        })
        .with('vehicleCharger', () => {
          const legacyEnrollment = chargerEnrollmentData?.find(
            (e) => e.charger_id === deviceId,
          )
          if (legacyEnrollment) {
            return {
              ...legacyEnrollment,
              utility_program_id:
                legacyEnrollment.utility_program_id?.toString(),
              unenrolled_reasons: '',
              unenrolled_source: '',
              group: legacyEnrollment.group ?? {},
            } as ChargerEnrollment
          }
          const enrollment = relevantEnrollments?.find(
            (e) => (e as ChargerEnrollment).charger_id === deviceId,
          )
          return enrollment
            ? {
                ...enrollment,
                group: enrollment.group ?? {},
              }
            : enrollment
        })
        .with('energySystem', () => {
          const legacyEnrollment = energySystemEnrollmentData?.find(
            (e) => e.energy_system_id === deviceId,
          )
          if (legacyEnrollment) {
            return {
              ...legacyEnrollment,
              utility_program_id:
                legacyEnrollment.utility_program_id?.toString(),
              unenrolled_reasons: '',
              unenrolled_source: '',
              group: legacyEnrollment.group ?? {},
            } as EnergySystemEnrollment
          }
          const enrollment = relevantEnrollments?.find(
            (e) => (e as EnergySystemEnrollment).energy_system_id === deviceId,
          )
          return enrollment
            ? {
                ...enrollment,
                group: enrollment.group ?? {},
              }
            : enrollment
        })
        .with('home', () => {
          const enrollment = relevantEnrollments?.find(
            (e) => (e as BehavioralEnrollment).profile_id === deviceId,
          )
          return enrollment
            ? {
                ...enrollment,
                group: enrollment.group ?? {},
              }
            : enrollment
        })
        .exhaustive()

      return {
        utilityProgram: p.utility_program,
        viewConfig: enrollment?.view_config_json ?? p.view_config_json,
        enrollment,
      }
    })
    .filter((item) => item.enrollment !== undefined)
    .filter((item) => {
      if (opts.includeExpired) {
        return true
      }
      return !getProgramEnrollmentExpiration(
        item.utilityProgram.preferred_display_timezone,
        item.viewConfig?.programCard?.enrollmentEndDate,
        item.viewConfig?.programCard?.rollingDeadline,
      ).isExpired
    })

  return {
    isLoading:
      isLoadingOrIdle ||
      vehicleEnrollmentIsLoadingOrIdle ||
      chargerEnrollmentIsLoadingOrIdle ||
      thermostatEnrollmentIsLoadingOrIdle ||
      energySystemEnrollmentIsLoadingOrIdle,
    deviceEnrollments,
  }
}

export function useDeviceEligibility(
  deviceType: DeviceType,
  deviceId: string | number,
) {
  const { data, isLoadingOrIdle } = programsNewCollection.useFetch()

  const deviceEligibilities = data
    ?.map((program) => {
      const deviceEligibility = program.device_eligibility_overview.devices[
        deviceTypeToEligibilityMap[deviceType]
      ]?.find((eligibility) => eligibility.id === deviceId)
      return {
        utilityProgram: program.utility_program,
        viewConfig: program.view_config_json,
        eligibility: deviceEligibility,
      }
    })
    .filter(
      (programInfo) =>
        !getProgramEnrollmentExpiration(
          programInfo.utilityProgram.preferred_display_timezone,
          programInfo.viewConfig?.programCard?.enrollmentEndDate,
          programInfo.viewConfig?.programCard?.rollingDeadline,
        ).isExpired,
    )

  return { deviceEligibilities, isLoading: isLoadingOrIdle }
}

export function useUserHasProgramEnrollment(programIds?: number[]) {
  const { data: programsNewData, isLoadingOrIdle } =
    programsNewCollection.useFetch()

  let programs = programsNewData

  if (programIds) {
    programs = programs.filter((program) =>
      programIds.includes(program.utility_program.id),
    )
  }

  const userHasProgramEnrollment = programs?.some((program) =>
    Boolean(program.enrollment_overview?.program_enrollment),
  )

  return {
    userHasProgramEnrollment,
    isLoading: isLoadingOrIdle,
  }
}
